import * as React from "react";
import { useTheme } from "../../providers/ThemeContext";

type FaqItem = { q: string; a: string };

type Props = {
  items: FaqItem[];
  defaultOpen?: number | number[] | "all" | null;
};

const Chevron: React.FC<{ open: boolean; color: string }> = ({
  open,
  color,
}) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    style={{
      transition: "transform .2s ease",
      transform: open ? "rotate(180deg)" : "rotate(0deg)",
      flexShrink: 0,
    }}
    aria-hidden="true"
  >
    <path
      d="M7 10l5 5 5-5"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FaqAccordion = ({ items, defaultOpen = null }: Props) => {
  const { theme } = useTheme();

  const initialOpenSet = React.useMemo(() => {
    if (defaultOpen === "all") return new Set(items.map((_, i) => i));
    if (Array.isArray(defaultOpen)) return new Set(defaultOpen);
    if (typeof defaultOpen === "number") return new Set([defaultOpen]);
    return new Set<number>();
  }, [defaultOpen, items.length]);

  const [openSet, setOpenSet] = React.useState<Set<number>>(initialOpenSet);

  React.useEffect(() => setOpenSet(initialOpenSet), [initialOpenSet]);

  const baseId = React.useId();

  const s = {
    wrap: { display: "grid", gap: 10 },
    item: {
      border: `1px solid ${theme.primary}33`,
      borderRadius: 12,
      background: theme.background ?? "#fff",
      overflow: "hidden",
    },
    // The heading wraps the button so each question is a real heading in the
    // document outline; the reset keeps the previous look.
    heading: { margin: 0, fontSize: "inherit", fontWeight: "inherit" },
    button: {
      display: "flex",
      width: "100%",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "12px 14px",
      cursor: "pointer",
      background: "transparent",
      border: "none",
      color: theme.text,
      textAlign: "start" as const,
      font: "inherit",
    },
    q: {
      fontWeight: 700 as const,
      fontFamily: "'Poppins', sans-serif",
      fontSize: 17,
      flex: 1,
      margin: 0,
    },
    body: {
      padding: "0 14px 12px 14px",
      fontSize: 14,
      color: theme.text,
      margin: 0,
    },
  } as const;

  const toggle = (i: number) =>
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  return (
    <div style={s.wrap}>
      {items.map((it, i) => {
        const open = openSet.has(i);
        const btnId = `${baseId}-q${i}`;
        const panelId = `${baseId}-a${i}`;
        return (
          <div key={i} style={s.item}>
            <h3 style={s.heading}>
              <button
                type="button"
                id={btnId}
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => toggle(i)}
                style={s.button}
              >
                <span style={s.q}>{it.q}</span>
                <Chevron open={open} color={theme.text} />
              </button>
            </h3>
            {/* hidden (not unmounted): the answer stays in the HTML for crawlers */}
            <div
              id={panelId}
              role="region"
              aria-labelledby={btnId}
              hidden={!open}
            >
              <p style={s.body}>{it.a}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FaqAccordion;
